import {clientFetch} from '../utils';
import type {AppContextOutput, ContextDefault, AppContextInstance, HttpClient} from '../types';
import {appContextOptions} from './appContextOptions';
import {createFetchContext, createLoadApiContext, createUseAppContext} from './context';
import {createAddLinks, defineCreateEndpoint, createEndoitExists, createGetEndpointUrl} from './endpoints';

export type CreateDefineAppContextArgs = {
    fetchStrategy: HttpClient;
};

/**
 * @warn only to bo used by Nuxt module
 */
export function createDefineAppContext({fetchStrategy}: CreateDefineAppContextArgs) {
    /**
     *   This tool will make it easier to bootstrap new Vue applications
     *   without needing `vuejs-starter`
     *
     *   All setup of context registry is now under just one function `defineAppContext`
     *
     *   What will this function do:
     *
     *   Exemple:
     *
     *   ```ts
     *   // src/registry/index.ts
     *   import {defineAppContext} from '@malt/vue-context';
     *
     *   type Entries = ['paymentContext','confirmationPage','cards'];
     *
     *   export const {loadApiContext, createEndpoint, useAppContext, endpointExists, getEndpointUrl} = defineAppContext<MyAppContextResource, Entries>(
     *       {
     *           baseUrl: '/api/my-app',
     *           // Called when context is fetched and stored
     *           onResolvedContext: async ({createEndpoint, addLinks, pinia}) => {
     *               // Exemple with more links to fetch from another endpoint
     *               const paymentContextStore = usePaymentContext(pinia);
     *               const paymentContextRequest = createEndpoint<PaymentContextResource>('paymentContext');
     *               const {_links, ...paymentContext} = await paymentContextRequest();
     *               paymentContextStore.$patch(paymentContext);
     *               addLinks(_links);
     *           },
     *       },
     *   );
     *  ```
     */
    function _defineAppContext<TContext extends Partial<ContextDefault>, TEntries extends string[]>(
        options: Omit<AppContextInstance<TContext, TEntries>, '$fetch'>,
    ): AppContextOutput<TContext, TEntries> {
        const appId = appContextOptions.addAppInstance({...options, $fetch: fetchStrategy});

        return {
            useAppContext: createUseAppContext(appId) as any,
            loadApiContext: createLoadApiContext(appId),
            reloadContext: createFetchContext(appId),
            addLinks: createAddLinks(appId),
            createEndpoint: defineCreateEndpoint(appId),
            endpointExists: createEndoitExists(appId),
            getEndpointUrl: createGetEndpointUrl(appId),
        };
    }

    return _defineAppContext;
}

export const defineAppContext = createDefineAppContext({fetchStrategy: clientFetch});
